import DeletePopover from "@/app/shared/delete-popover";
import EyeIcon from "@/components/icons/eye";
import PencilIcon from "@/components/icons/pencil";
import { HeaderCell } from "@/components/ui/table";
import { routes } from "@/config/routes";
import { SortConfig } from "@/hooks/useTableServer";
import { EPromotionStatus, IPromotion } from "@/utilities/types/Promotion";
import dayjs from "dayjs";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ActionIcon, Button, Modal, Tooltip } from "rizzui";
import StatusPromotion from "./status-promotion";
import TicketIcon from "@/components/icons/ticket";

type PromotionSortBy =
  | "code"
  | "discount"
  | "status"
  | "createdAt"
  | "startDate"
  | "endDate";

type Columns = {
  data: IPromotion[];
  sortConfig?: SortConfig<PromotionSortBy>;
  onDeleteItem: (id: string) => void;
  onHeaderCellClick?: (value: string) => void;
  handleReset: () => void;
  refresh?: () => void;
  onUpdatePromotion: (id: string, data: Partial<IPromotion>) => Promise<void>;
};

// Promotion Details Modal Component
const PromotionDetailsModal = ({
  isOpen,
  onClose,
  promotionData,
}: {
  isOpen: boolean;
  onClose: () => void;
  promotionData: IPromotion;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">Promotion Details</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <h3 className="font-semibold text-lg">{promotionData.code}</h3>
            <p className="text-gray-500">
              {promotionData.description || "No description"}
            </p>
          </div>

          <div className="border-t pt-4 mt-2 col-span-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Discount</p>
                <p className="font-medium">
                  {promotionData.discount || 0}
                  {promotionData.discountType === "PERCENT" ? "%" : "$"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="font-medium">{promotionData.status}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Start Date</p>
                <p className="font-medium">
                  {promotionData.startDate
                    ? dayjs(promotionData.startDate).format("MMMM DD, YYYY")
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">End Date</p>
                <p className="font-medium">
                  {promotionData.endDate
                    ? dayjs(promotionData.endDate).format("MMMM DD, YYYY")
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Type</p>
                <p className="font-medium">{promotionData.type || "-"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Vouchers</p>
                <p className="font-medium">
                  {promotionData.numberOfVoucher || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Simple view button component with its own state
const PromotionViewButton = ({
  promotionData,
}: {
  promotionData: IPromotion;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      <Tooltip
        size="sm"
        content={"View Promotion"}
        placement="top"
        color="invert"
      >
        <ActionIcon size="sm" variant="outline" onClick={openModal}>
          <EyeIcon className="h-4 w-4" />
        </ActionIcon>
      </Tooltip>

      {isModalOpen && (
        <PromotionDetailsModal
          isOpen={isModalOpen}
          onClose={closeModal}
          promotionData={promotionData}
        />
      )}
    </>
  );
};

export const getColumns = ({
  sortConfig,
  onDeleteItem,
  onHeaderCellClick,
  onUpdatePromotion,
}: Columns) => {
  return [
    {
      title: (
        <HeaderCell
          title="Promo code"
          sortable
          align="left"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "code"
          }
        />
      ),
      dataIndex: "code",
      key: "code",
      onHeaderCell: () => onHeaderCellClick?.("code"),
      render: (code: string) => {
        return <span className="font-medium">{code}</span>;
      },
    },

    {
      title: (
        <HeaderCell
          title="Discount"
          sortable
          align="right"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "discount"
          }
        />
      ),
      dataIndex: "discount",
      key: "discount",
      align: "right",
      onHeaderCell: () => onHeaderCellClick?.("discount"),
      render: (discount: number, row: IPromotion) => {
        return (
          <span>
            {discount || 0} {row.discountType === "PERCENT" ? "%" : "$"}
          </span>
        );
      },
    },

    {
      title: (
        <HeaderCell
          title="Start Date"
          sortable
          align="center"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "startDate"
          }
        />
      ),
      dataIndex: "startDate",
      key: "startDate",
      align: "center",
      onHeaderCell: () => onHeaderCellClick?.("startDate"),
      render: (date: string) => {
        return date ? dayjs(date).format("MMMM DD, YYYY") : "-";
      },
    },

    {
      title: (
        <HeaderCell
          title="End Date"
          sortable
          align="center"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "endDate"
          }
        />
      ),
      dataIndex: "endDate",
      key: "endDate",
      align: "center",
      onHeaderCell: () => onHeaderCellClick?.("endDate"),
      render: (date: string) => {
        return date ? dayjs(date).format("MMMM DD, YYYY") : "-";
      },
    },

    {
      title: (
        <HeaderCell
          title="Status"
          sortable
          align="left"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "status"
          }
        />
      ),
      dataIndex: "status",
      key: "status",
      align: "center",
      onHeaderCell: () => onHeaderCellClick?.("status"),
      render: (_: string, row: IPromotion) => {
        return (
          <StatusPromotion
            onChange={async (value: EPromotionStatus | void) => {
              if (value) {
                await onUpdatePromotion(row.id, {
                  status: value,
                });
              }
            }}
            status={row.status}
            className="w-28 text-center"
          />
        );
      },
    },

    {
      title: (
        <HeaderCell
          className="ps-3"
          title={<span className="whitespace-nowrap">Actions</span>}
        />
      ),
      dataIndex: "action",
      key: "action",
      width: 170,
      render: (_: string, row: IPromotion) => {
        return (
          <div className="flex items-center justify-end gap-3 pe-4">
            <PromotionViewButton promotionData={row} />

            <Tooltip
              size="sm"
              content={"View Usage History"}
              placement="top"
              color="invert"
            >
              <Link
                to={routes.voucher.byPromotion(row.id)}
                state={{ promotionName: row.code }}
              >
                <ActionIcon size="sm" variant="outline">
                  <TicketIcon className="h-4 w-4" />
                </ActionIcon>
              </Link>
            </Tooltip>

            <Tooltip
              size="sm"
              content={"Edit Promotion"}
              placement="top"
              color="invert"
            >
              <Link
                to={routes.promotion.edit(row.id)}
                state={{ rowKey: row?.id }}
              >
                <ActionIcon size="sm" variant="outline">
                  <PencilIcon className="h-4 w-4" />
                </ActionIcon>
              </Link>
            </Tooltip>

            <DeletePopover
              title={`Delete Promotion?`}
              description={`Are you sure you want to delete "${row.code}"?`}
              onDelete={() => onDeleteItem(row.id)}
            />
          </div>
        );
      },
    },
  ];
};
